import { useState, type ReactNode } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Icon } from "@/components/ui/Icon";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { ProjectionLag } from "@/components/ui/ProjectionLag";
import {
  useProjectionStatus,
  useReport,
  type ReportFilters,
  type ReportName,
} from "@/lib/api/reports";
import { dateTime, duration, qty, relative, shortId } from "@/lib/format";
import { columnLabel } from "@/lib/labels";
import { useList } from "@/lib/api/queries";

type Row = Record<string, string>;

/** Ustunni shaklidan kelib chiqib chizish. */
/** Xodimga ishora qiladigan ustunlar — ularda ID o'rniga ism chiqadi. */
const PERSON_COLUMNS = ["employee_id", "assignee_id", "manager_id", "counted_by", "confirmed_by"];

function renderCell(column: string, row: Row, people: Map<string, string>) {
  const value = row[column] ?? "";
  if (!value.trim()) return <span style={{ color: "var(--outline)" }}>—</span>;

  // Xodim: UUID prefiksi hamma yozuvda bir xil (UUIDv7), shuning uchun
  // qisqartirilgan ID hech narsani ajratmasdi.
  if (PERSON_COLUMNS.includes(column)) {
    const name = people.get(value);
    return name ? <span title={value}>{name}</span> : <span className="mono" title={value}>{shortId(value)}</span>;
  }
  // Soniya emas, «1 soat 20 daqiqa».
  if (column.endsWith("_seconds")) {
    return <span className="num">{duration(value)}</span>;
  }

  if (
    column === "status" ||
    column.endsWith("_status") ||
    column.endsWith("_type") ||
    column.endsWith("_note") ||
    column === "process"
  ) {
    return <StatusBadge value={value} />;
  }
  // σ/μ foizda: «0.1834» dan «18.3%» tezroq o'qiladi.
  if (column === "variation_coefficient") {
    return <span className="num">{(Number(value) * 100).toFixed(1)}%</span>;
  }
  if (column.endsWith("_at")) {
    return <span title={dateTime(value)}>{relative(value)}</span>;
  }
  if (column.endsWith("_qty") || column.endsWith("_count") || column.endsWith("_slots")) {
    return <span className="num">{qty(value)}</span>;
  }
  if (column.endsWith("_id") && value.length > 20) {
    return (
      <span className="mono" title={value}>
        {shortId(value)}
      </span>
    );
  }
  if (value === "true" || value === "false") {
    return (
      <StatusBadge
        value={value === "true" ? "ha" : "yo'q"}
        tone={value === "true" ? "info" : "neutral"}
      />
    );
  }
  return value;
}

const NUMERIC = /_qty$|_count$|_slots$|_seconds$|_kg$|_coefficient$/;

interface ReportViewProps {
  report: ReportName;
  title: string;
  hint: string;
  /** Faqat muammoli satrlarni ko'rsatish tugmasi mos keladimi. */
  supportsIssuesFilter?: boolean;
  /** Tashqaridan berilgan server filtrlari (masalan matritsa katagi). */
  filters?: ReportFilters;
  /** Sarlavha bilan jadval orasidagi blok (masalan matritsa). */
  above?: ReactNode;
}

/**
 * Umumiy hisobot ko'rinishi.
 *
 * `reporting-service` ustunlar ro'yxatini O'ZI beradi
 * (`GET /reports`), shuning uchun ustunlar interfeysda qo'lda
 * yozilmaydi: hisobotga yangi ustun qo'shilsa u o'z-o'zidan
 * ko'rinadi va ro'yxat ikki joyda saqlanmaydi.
 */
export function ReportView({
  report,
  title,
  hint,
  supportsIssuesFilter,
  filters,
  above,
}: ReportViewProps) {
  const [search, setSearch] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);

  const query = useReport(report, {
    limit: 200,
    ...filters,
    ...(supportsIssuesFilter && onlyIssues ? { only_issues: true } : {}),
  });
  const status = useProjectionStatus();

  const result = query.data;
  const needle = search.trim().toLowerCase();
  const rows = (result?.items ?? []).filter((row) =>
    needle
      ? Object.values(row).some((value) => value.toLowerCase().includes(needle))
      : true,
  );

  // Xodim ismlari BITTA so'rovda: jadvalda o'nlab satr bo'lishi mumkin.
  const personIds = [
    ...new Set(
      rows.flatMap((row) =>
        PERSON_COLUMNS.map((column) => row[column]).filter(
          (value): value is string => typeof value === "string" && value.length > 20,
        ),
      ),
    ),
  ];
  const users = useList<{ id: string; full_name: string; username: string }>(
    "identity",
    "/api/v1/users",
    { ids: personIds.join(","), limit: 200 },
    { enabled: personIds.length > 0 },
  );
  const people = new Map<string, string>(
    (users.data?.items ?? []).map((user) => [user.id, user.full_name || user.username] as const),
  );

  const columns: Array<Column<Row>> = (result?.columns ?? []).map((column) => ({
    key: column,
    header: columnLabel(column),
    align: NUMERIC.test(column) ? "right" : undefined,
    render: (row) => renderCell(column, row, people),
  }));

  return (
    <>
      <PageHeader
        title={title}
        hint={hint}
        actions={<ProjectionLag status={status.data?.items} />}
      />

      {above}

      <div className="toolbar">
        <div className="toolbar-grow search-box">
          <Icon name="search" size={16} className="icon-inside" />
          <input
            className="input"
            placeholder="Qatorlar ichidan qidirish…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {supportsIssuesFilter && (
          <button
            type="button"
            className={onlyIssues ? "btn btn-tonal" : "btn btn-outlined"}
            onClick={() => setOnlyIssues((value) => !value)}
          >
            <Icon name="filter" size={16} />
            Faqat muammolilar
          </button>
        )}

        <button
          type="button"
          className="btn btn-outlined"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          <Icon name="refresh" size={16} />
          Yangilash
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(_, index) => String(index)}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => void query.refetch()}
        emptyTitle={needle ? "Qidiruv natijasi bo'sh" : "Ma'lumot yo'q"}
        emptyHint={
          needle
            ? "Boshqa so'z bilan qidirib ko'ring."
            : "Bu kesim bo'yicha hali hodisa qayd etilmagan."
        }
        hasMore={result?.has_more}
        footer={
          <span>
            {rows.length} qator
            {needle && result ? ` (${result.items.length} tadan)` : ""}
          </span>
        }
      />
    </>
  );
}
